<script setup>
import { computed } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextLabel from 'dashboard/components-next/label/Label.vue';

// Formulário compartilhado de criar e editar etiqueta: prévia igual à da lista de
// conversas, paleta de cores prontas (com cor livre) e exibição no menu lateral.
const props = defineProps({
  headerTitle: { type: String, required: true },
  headerDescription: { type: String, default: '' },
  submitLabel: { type: String, required: true },
  titleError: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  submitDisabled: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel', 'titleBlur']);

const title = defineModel('title', { type: String, default: '' });
const description = defineModel('description', { type: String, default: '' });
const color = defineModel('color', { type: String, default: '#3B82F6' });
const showOnSidebar = defineModel('showOnSidebar', {
  type: Boolean,
  default: true,
});

const PRESET_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#14B8A6',
  '#06B6D4',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#64748B',
];

const normalizedColor = computed(() => (color.value || '').toUpperCase());
const isPresetColor = computed(() =>
  PRESET_COLORS.includes(normalizedColor.value)
);

const previewLabel = computed(() => ({
  title: title.value || props.headerTitle,
  color: color.value,
  description: description.value,
}));

const onTitleInput = value => {
  title.value = String(value || '').toLowerCase();
};
</script>

<template>
  <form
    class="flex flex-col"
    data-testid="label-form"
    @submit.prevent="emit('submit')"
  >
    <header class="flex items-start gap-3 px-6 pt-6 pb-4">
      <span
        class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-slate-11"
        aria-hidden="true"
      >
        <span class="i-hugeicons-tag-01 size-5" />
      </span>
      <div class="min-w-0">
        <h2 class="m-0 text-base font-semibold text-n-slate-12">
          {{ headerTitle }}
        </h2>
        <p v-if="headerDescription" class="m-0 text-sm text-n-slate-11">
          {{ headerDescription }}
        </p>
      </div>
    </header>

    <div class="flex flex-col gap-5 px-6 pb-5">
      <section
        class="flex items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 dark:border-n-strong"
        data-testid="label-preview"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('LABEL_MGMT.FORM.PREVIEW') }}
        </span>
        <NextLabel :label="previewLabel" compact color-border />
      </section>

      <div data-testid="label-title">
        <Input
          :model-value="title"
          :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
          :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
          :message="titleError || $t('LABEL_MGMT.FORM.NAME.HINT')"
          :message-type="titleError ? 'error' : 'info'"
          custom-input-class="lowercase"
          autofocus
          @update:model-value="onTitleInput"
          @blur="emit('titleBlur')"
        />
      </div>

      <div data-testid="label-description">
        <TextArea
          v-model="description"
          :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
          :max-length="200"
          show-character-count
          min-height="3.5rem"
        />
      </div>

      <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
        <legend class="mb-2 p-0 text-sm font-medium text-n-slate-12">
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
        </legend>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="preset in PRESET_COLORS"
            :key="preset"
            type="button"
            class="grid size-7 place-items-center rounded-full outline-none ring-offset-2 ring-offset-n-solid-1 transition focus-visible:ring-2 focus-visible:ring-n-brand"
            :class="
              normalizedColor === preset
                ? 'ring-2 ring-n-slate-12'
                : 'hover:scale-110'
            "
            :style="{ backgroundColor: preset }"
            :aria-label="preset"
            :aria-pressed="normalizedColor === preset"
            data-testid="label-color-preset"
            @click="color = preset"
          >
            <span
              v-if="normalizedColor === preset"
              class="i-hugeicons-tick-02 size-4 text-white"
              aria-hidden="true"
            />
          </button>
          <div
            class="flex items-center gap-2 rounded-full border py-1 pl-1 pr-3"
            :class="
              isPresetColor
                ? 'border-n-weak dark:border-n-strong'
                : 'border-n-slate-12'
            "
          >
            <woot-color-picker v-model="color" class="label-form-picker" />
            <span
              class="text-xs font-medium text-n-slate-11"
              :class="{ uppercase: !isPresetColor }"
            >
              {{ isPresetColor ? $t('LABEL_MGMT.FORM.COLOR.CUSTOM') : color }}
            </span>
          </div>
        </div>
      </fieldset>

      <label
        class="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-n-weak p-4 dark:border-n-strong"
      >
        <span class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
          </span>
          <span class="block text-xs text-n-slate-11">
            {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.HINT') }}
          </span>
        </span>
        <Switch v-model="showOnSidebar" data-testid="label-show-on-sidebar" />
      </label>
    </div>

    <footer
      class="flex items-center justify-end gap-2 border-t border-n-weak px-6 pt-4 pb-2 dark:border-n-strong"
    >
      <NextButton
        faded
        slate
        type="button"
        :label="$t('LABEL_MGMT.FORM.CANCEL')"
        data-testid="label-cancel"
        @click="emit('cancel')"
      />
      <NextButton
        type="submit"
        data-testid="label-submit"
        :label="submitLabel"
        :disabled="submitDisabled"
        :is-loading="isLoading"
      />
    </footer>
  </form>
</template>

<style lang="scss" scoped>
// O seletor livre é o mesmo do sistema; aqui só ajusta o quadrado ao tamanho das bolinhas.
.label-form-picker {
  :deep(.colorpicker--selected) {
    @apply mb-0 size-5 rounded-full;
  }
}
</style>
